import {CloseOutlined} from '@ant-design/icons'
import {Button, Checkbox, Input, InputNumber, Segmented} from 'antd'
import {getId} from 'apis/model/base'
import FieldDecorator from 'components/form/FieldDecorator'
import FieldsFactory from 'components/form/FieldsFactory'
import {StepsForm} from 'components/form/StepsForm'
import {createValue} from 'components/form/utils'
import Pure from 'components/Pure'
import {Field, useFormikContext} from 'formik'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useEffect, useState} from 'react'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify,} from 'views/AppLocals/factory/createNotification'
import {poll_edit_Api} from '../../apis'
import {FullSideModalLayout} from '../../components/Modal/SideModalLayout'
import {formatterNumber} from '../../helpers/formatterNumber'
import {parseNumber} from '../../helpers/parseNumber'
import useAsyncAction from '../../modules/asyncCache/useAsyncAction'
import ServerContext from '../Server/ServerContext'
import TooltipApp from "components/TooltipApp";
import {ifThenElseMultipleConditions} from "../../helpers/ifThenElseMultipleConditions";
import PostSettingForm from "../../components/Feed/PostForm/PostSettingForm";
import PostFormContext from "../../components/Feed/PostForm/PostFormContext";

const Answers = ({onChange, defaultValue = [], isQuiz = false}) => {
    const t = useTranslate()
    // Gán stable key cho mỗi answer ngay từ đầu để tránh key={index} gây lệch DOM
    const [answers, setAnswers] = useState(() =>
        defaultValue.map((ans, i) => ({
            ...ans,
            _key: ans.id != null ? String(ans.id) : `new_${i}_${Date.now()}`,
        }))
    )
    const [newAnswer, setNewAnswer] = useState('')

    useEffect(() => {
        onChange(answers)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answers])

    const handleAddNewAnswer = (e) => {
        e.preventDefault()
        if (!newAnswer.trim()) return
        setAnswers([
            ...answers,
            {id: null, title: newAnswer, is_correct: 0, _key: `new_${Date.now()}`},
        ])
        setNewAnswer('')
    }

    const handleRemoveAnswer = (key) => {
        setAnswers(answers.filter((ans) => ans._key !== key))
    }

    const handleChangeTitle = (key, value) => {
        setAnswers(answers.map((ans) =>
            ans._key === key ? {...ans, title: value} : ans
        ))
    }

    const handleToggleCorrect = (key, checked) => {
        setAnswers(answers.map((ans) => ({
            ...ans,
            is_correct: ans._key === key ? (checked ? 1 : 0) : ans.is_correct ?? 0,
        })))
    }

    return (
        <>
            {answers.map((answer) => {
                const isCorrect = !!answer.is_correct
                return (
                    <div
                        key={answer._key}
                        className="animated fadeInUp faster"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            marginBottom: 8,
                            padding: '4px 8px',
                            borderRadius: 6,
                            border: `1px solid ${isQuiz
                                ? isCorrect
                                    ? '#52c41a'
                                    : '#d9d9d9'
                                : '#d9d9d9'}`,
                            background: isQuiz && isCorrect
                                ? '#f6ffed'
                                : 'transparent',
                            transition: 'all 0.2s ease',
                        }}>
                        {isQuiz && (
                            <TooltipApp title={t('correct answer')}>
                                <Checkbox
                                    checked={isCorrect}
                                    onChange={(e) => handleToggleCorrect(answer._key, e.target.checked)}
                                    style={{
                                        flexShrink: 0,
                                        accentColor: '#52c41a',
                                    }}
                                />
                            </TooltipApp>
                        )}
                        <Input
                            bordered={false}
                            style={{flex: 1, padding: '4px 0'}}
                            // controlled value thay vì defaultValue để React luôn sync đúng
                            value={answer.title}
                            onChange={(e) => handleChangeTitle(answer._key, e.target.value)}
                            suffix={
                                <TooltipApp title={t('remove')}>
                                    <CloseOutlined
                                        onClick={() => handleRemoveAnswer(answer._key)}
                                        style={{
                                            color: '#bfbfbf',
                                            fontSize: 12,
                                            cursor: 'pointer',
                                            transition: 'color 0.2s',
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.color = '#ff4d4f'}
                                        onMouseLeave={(e) => e.currentTarget.style.color = '#bfbfbf'}
                                    />
                                </TooltipApp>
                            }
                        />
                    </div>
                )
            })}
            <form onSubmit={handleAddNewAnswer}>
                <div className="flex gap-3">
                    <Input.Group
                        compact
                        style={{display: 'inline-flex', alignItems: 'center'}}
                        className="flex items-center">
                        <Input
                            key="create new answer"
                            onChange={(e) => setNewAnswer(e.target.value)}
                            placeholder={t('input answer')}
                            value={newAnswer}
                        />
                        <TooltipApp
                            placement="topLeft"
                            title={t('add answers to poll questions')}>
                            <Button
                                type="primary"
                                onClick={handleAddNewAnswer}>
                                {t('add')}
                            </Button>
                        </TooltipApp>
                    </Input.Group>
                </div>
            </form>
        </>
    )
}

const PollForm = ({onBack, onChange}) => {
    const t = useTranslate()
    const {values} = useFormikContext()
    return (
        <div className="flex flex-col gap-4">

            {/* 1. Channel */}
            <PostSettingForm/>

            {/* 2. Câu hỏi — full width */}
            <Pure>
                <FieldsFactory
                    formSchema={[
                        {
                            children: {
                                type: 'title',
                                name: 'title',
                                label: t('questions/voting content'),
                                component: Input,
                                withProps: {
                                    type: 'text',
                                    placeholder: t('questions/ content'),
                                },
                            },
                        },
                    ]}
                />
            </Pure>

            {/* 3. Loại + Đúng/Sai — flex-col, description dài không bị chèn */}
            <div className="flex flex-col gap-3">
                <Field
                    name="poll_type"
                    render={({field}) => (
                        <FieldDecorator
                            className="flex flex-col gap-1"
                            name="poll_type"
                            labelProps={{className: undefined}}
                            hasError={false}
                            label="poll / quiz"
                            description="poll / quiz description">
                            <Segmented
                                className="w-fit custom-segmented"
                                defaultValue={_.get(values, 'poll_type', 0)}
                                options={[
                                    {label: t('poll'), value: "poll"},
                                    {label: t('quiz'), value: "quiz"},
                                ]}
                                onChange={(value) => field.onChange(createValue('poll_type', value))}
                            />
                        </FieldDecorator>
                    )}
                />
                <Field
                    name="is_yesno_question"
                    render={({field}) => (
                        <FieldDecorator
                            className="flex flex-col gap-1"
                            name="is_yesno_question"
                            labelProps={{className: undefined}}
                            hasError={false}
                            label={t('is yes/no question?')}
                            description={t('yes/no questions description')}>
                            <Segmented
                                className="w-fit custom-segmented"
                                defaultValue={_.get(values, 'is_yesno_question', 0)}
                                options={[
                                    {label: t('yes'), value: 1},
                                    {label: t('no'), value: 0},
                                ]}
                                onChange={(value) =>
                                    field.onChange(createValue('is_yesno_question', value))
                                }
                            />
                        </FieldDecorator>
                    )}
                />
            </div>

            {/* 4. Cách bình chọn — InputNumber inline ngay cạnh khi cần */}
            <Field
                name="is_single_selection"
                render={({field}) => (
                    <FieldDecorator
                        className="flex flex-col gap-1"
                        name="is_single_selection"
                        labelProps={{className: undefined}}
                        hasError={false}
                        label={t('number of possible answers in a poll')}>
                        <div className="flex items-center gap-3 flex-wrap">
                            <Segmented
                                className="w-fit custom-segmented"
                                disabled={!!_.get(values, 'is_yesno_question', 0) || (_.get(values, 'poll_type', 'poll') === 'quiz')}
                                defaultValue={_.get(values, 'is_single_selection', 1)}
                                value={
                                    ifThenElseMultipleConditions(
                                        _.get(values, 'is_single_selection', 1),
                                        !!_.get(values, 'is_yesno_question', 0), 1,
                                        (_.get(values, 'poll_type', 'poll') === 'quiz'), 1
                                    )
                                }
                                options={[
                                    {label: t('one answer'), value: 1},
                                    {label: t('multiple answer'), value: 0},
                                ]}
                                onChange={(value) =>
                                    field.onChange(createValue('is_single_selection', value))
                                }
                            />
                            {!!!_.get(values, 'is_yesno_question', 0) &&
                            !!!_.get(values, 'is_single_selection') && (
                                <Field
                                    name="vote_on_maximum"
                                    render={({field: f, form}) => (
                                        <InputNumber
                                            min={1}
                                            max={_.get(form, 'values.answers', 1)}
                                            name="vote_on_maximum"
                                            inputMode="numeric"
                                            style={{width: 72}}
                                            formatter={formatterNumber}
                                            parser={parseNumber}
                                            defaultValue={1}
                                            value={
                                                !!_.get(values, 'is_yesno_question', 0)
                                                    ? 1
                                                    : _.get(values, 'vote_on_maximum', 1)
                                            }
                                            onChange={(value) =>
                                                f.onChange(createValue('vote_on_maximum', value))
                                            }
                                        />
                                    )}
                                />
                            )}
                        </div>
                    </FieldDecorator>
                )}
            />

            {/* 5. Đáp án — nhóm riêng */}
            {!!!_.get(values, 'is_yesno_question', 0) && (
                <div
                    className="flex flex-col gap-2"
                    style={{
                        border: '1px solid var(--border-color-50, #efefef)',
                        borderRadius: 12,
                        padding: 12,
                    }}>
                    <Field
                        name="answers"
                        render={({
                                     field,
                                     form: {values = {}, touched = {}, errors = {}} = {
                                         touched: {},
                                         errors: {},
                                     },
                                 }) => (
                            <FieldDecorator
                                errors={errors.answers}
                                labelProps={{className: undefined}}
                                label={t('answers can be voted on')}
                                description={_.get(values, 'poll_type') === 'quiz' ? t('mark correct answer hint') : undefined}>
                                <div className="flex flex-col">
                                    <Answers
                                        onChange={(value) => {
                                            field.onChange(createValue('answers', value))
                                        }}
                                        defaultValue={_.get(values, 'answers', [])}
                                        isQuiz={_.get(values, 'poll_type') === 'quiz'}
                                    />
                                </div>
                            </FieldDecorator>
                        )}
                    />
                    <Field
                        name="customizable_answers"
                        render={({field}) => (
                            <Checkbox
                                className="text-sm text-color-300 italic"
                                disabled={!!_.get(values, 'is_yesno_question', 0) || (_.get(values, 'poll_type', 'poll') === 'quiz')}
                                checked={Number(field.value)}
                                onChange={(e) =>
                                    field.onChange(
                                        createValue(
                                            'customizable_answers',
                                            e.target.checked ? '1' : '0'
                                        )
                                    )
                                }>
                                {t('allow add new answers')}
                            </Checkbox>
                        )}
                    />
                </div>
            )}
        </div>
    )
}

const CreatePollRoutes = [
    {
        path: '/questtionAndAnswers',
        name: 'edit a poll',
        component: PollForm,
        exact: true,
    },
]

const tranformBeforeSubmit = (values = {}) => {
    return {
        ...values,
        vote_on_maximum:
            !!values.is_single_selection || !!values.is_yesno_question
                ? 1
                : values.vote_on_maximum,
        answers: JSON.stringify(
            _.get(values, 'answers', []).map((obj) => ({
                id: obj.id,
                title: obj.title,
                is_correct: obj.is_correct ?? 0,   // <-- thêm field này
            }))
        ),
    }
}

export const EditPoll = ({
                             initialValues = {
                                 title: '',
                                 poll_type: 'poll',
                                 vote_on_maximum: 1,
                                 customizable_answers: 0,
                                 is_single_selection: 1,
                                 is_yesno_question: 0,
                                 answers: [],
                             },
                             onSubmit,
                             onCancel,
                             isLoading,
                         }) => {
    const t = useTranslate()
    const handleSubmit = (values) => {
        const {owner, container, ...rest} = values ?? {}
        onSubmit(tranformBeforeSubmit(rest))
    }
    const validate = (values) => {
        const {title, is_yesno_question} = values ?? {}
        const answers = Array.from(values?.answers ?? [])
        let errors = {}
        if (_.isEmpty(String(title ?? '').trim())) {
            errors.title = t('required field')
        }
        if (!is_yesno_question && !answers?.length) {
            errors.answers = t('required field')
        }
        return errors
    }
    return (
        <StepsForm
            validate={validate}
            isLoading={isLoading}
            onSubmit={handleSubmit}
            routes={CreatePollRoutes}
            initialValues={initialValues}
            onCancel={onCancel}
        />
    )
}

export const EnhancedEditPoll = ({pollItem, onCancel}) => {
    const t = useTranslate()
    const {item, setCurrent} = useContext(ServerContext)
    const {options} = useContext(PostFormContext)
    const {
        time,
        isLoading,
        handleAsyncAction: handleAsyncPost,
    } = useAsyncAction({
        apiInfo: poll_edit_Api,
        query: {
            ':id': pollItem.id,
        },
        onSuccess: (result) => {
            successNotify(NotificationActionTypes.UPDATE, t)
            setCurrent(null)
            onCancel()
        },
        onError: notifyOnError(t),
    })

    return (
        <FullSideModalLayout
            right
            renderHeader={() => null}
            onCancel={() => {
                setCurrent(null)
                onCancel()
            }}>
            <div className="p-3">
                {/* Header — đồng bộ design system */}
                <div
                    className="flex items-center justify-between"
                    style={{
                        paddingBottom: 12,
                        marginBottom: 12,
                        borderBottom: '1px solid var(--border-color-50, #f0f0f0)',
                    }}>
                    <button
                        type="button"
                        onClick={() => { setCurrent(null); onCancel() }}
                        style={{
                            fontSize: 13,
                            color: 'var(--color-text-400, #888)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            minWidth: 48,
                            padding: '4px 0',
                        }}>
                        {t('close')}
                    </button>
                    <span style={{
                        flex: 1,
                        textAlign: 'center',
                        fontSize: 14,
                        fontWeight: 700,
                        color: 'var(--color-text-000)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                    }}>
                        {t('edit post poll')}
                    </span>
                    <div style={{minWidth: 48}}/>
                </div>
                <EditPoll
                    initialValues={{
                        containers: options,
                        owner_disabled: true,
                        owner: pollItem?.owner,
                        container_disabled: true,
                        privacy: pollItem?.privacy,
                        container: pollItem?.container,
                        default_owner_id: getId(pollItem?.owner),
                        default_container_id: getId(pollItem?.container),
                        title: _.get(pollItem, 'title'),
                        description: _.get(pollItem, 'description'),
                        poll_type: _.get(pollItem, 'poll_type'),
                        vote_on_maximum: _.get(pollItem, 'vote_on_maximum', 1),
                        answers: _.get(pollItem, 'poll_answers', []),
                        customizable_answers: _.get(pollItem, 'customizable_answers'),
                        is_single_selection:
                            _.get(pollItem, 'vote_on_maximum', 1) === 1 ||
                            _.get(pollItem, 'is_yesno_question') === 1
                                ? 1
                                : 0,
                        is_yesno_question: _.get(pollItem, 'is_yesno_question'),
                    }}
                    title={t('edit')}
                    isLoading={isLoading}
                    onSubmit={(values) => {
                        const {
                            owner,
                            container,
                            containers,
                            owner_disabled,
                            default_owner_id,
                            container_disabled,
                            default_container_id,
                            ...rest
                        } = values ?? {}
                        handleAsyncPost(rest)
                    }}
                    onCancel={() => {
                        setCurrent(null)
                        onCancel()
                    }}
                />
            </div>
        </FullSideModalLayout>
    )
}